import React, { useState, useEffect, useCallback } from "react";

import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { GatsbyImage } from "gatsby-plugin-image";
import { PortableText } from "@portabletext/react";
import { Link } from "gatsby";

const ProductsSection = ({ data }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: "start",
      dragFree: true,
    },
    [Autoplay()]
  );
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);

  const list = data.products || [];

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  const scrollPrev = useCallback(
    () => emblaApi && emblaApi.scrollPrev(),
    [emblaApi]
  );
  const scrollNext = useCallback(
    () => emblaApi && emblaApi.scrollNext(),
    [emblaApi]
  );

  useEffect(() => {
    if (!emblaApi) return;

    onSelect();
    emblaApi.on("select", onSelect);

    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <div className="py-16 md:py-24 xl:py-32 overflow-hidden">
      <div className="mx-auto px-5 md:px-8 lg:px-12">
        <div className="mb-8 md:mb-12 lg:mb-16">
          <h2 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-black leading-tight">
            {data.title}
          </h2>
        </div>

        <div className="w-full h-px bg-gray-300 mb-10 md:mb-16"></div>

        <div className="relative">
          <div className="flex absolute right-5 -top-10 space-x-4">
            <button
              onClick={scrollPrev}
              disabled={!canScrollPrev}
              className="w-12 h-12 rounded-full bg-white text-black flex items-center justify-center border border-gray-200 disabled:opacity-50 shadow-sm transition-all hover:shadow-md"
              aria-label="Previous slide"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 18L9 12L15 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              onClick={scrollNext}
              disabled={!canScrollNext}
              className="w-12 h-12 rounded-full bg-white text-black flex items-center justify-center border border-gray-200 disabled:opacity-50 shadow-sm transition-all hover:shadow-md"
              aria-label="Next slide"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 6L15 12L9 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
          <div className="overflow-hidden pt-10" ref={emblaRef}>
            <div className="flex">
              {[...list, ...list].map((item, key) => (
                <div
                  className="flex-[0_0_90%] md:flex-[0_0_45%] lg:flex-[0_0_30%] min-w-0 mr-6"
                  key={key + 3}
                >
                  <Link
                    to={item?.link?.current}
                    className="block h-full"
                    // target="_blank"
                  >
                    <div
                      className={`group h-full rounded-xl  shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer ${
                        (key + 3) % 3 === 0
                          ? "bg-[#2B322F] text-white"
                          : (key + 3) % 3 === 1
                          ? "bg-[#a6fee0] text-black"
                          : "bg-[#dbdbdb] text-black"
                      }`}
                    >
                      {/* Image Container with Hover Effects */}
                      <div className="relative mb-6 overflow-hidden rounded-xl">
                        <GatsbyImage
                          image={item.thumbnail?.asset?.gatsbyImageData}
                          alt={item.thumbnail?.altText || item?.title}
                          className="h-60 object-cover w-full rounded-xl group-hover:scale-105 transition-transform duration-500"
                        />
                        {/* Overlay on hover */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                      </div>

                      {/* Content */}
                      <div className="p-6">
                        <h3 className="text-2xl font-bold mb-6 leading-tight group-hover:opacity-90 transition-opacity duration-200">
                          {item?.title}
                        </h3>
                        <div className="text-lg opacity-80 mb-3 group-hover:opacity-70 transition-opacity duration-200">
                          <PortableText value={item._rawDescription} />
                        </div>

                        {/* Read More Indicator */}
                        <div className="flex items-center mt-4 opacity-0 group-hover:opacity-100 transition-all duration-200 transform translate-y-2 group-hover:translate-y-0">
                          <span className="text-base font-medium">
                            Read More
                          </span>
                          <svg
                            className="ml-2 w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-200"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductsSection;
